import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles, LogOut, MessageSquare } from 'lucide-react';
import useAuthStore from '../store/authStore';
import { getAttention, clearAttention } from '../api/chatAI';
import LeftPanel from './LeftPanel';
import ChatPanel from './ChatPanel';
import TeamChatPanel from './TeamChatPanel';
import ProfilePanel from './ProfilePanel';
import InboxModal from './InboxModal';
import AttentionPanel from './AttentionPanel';
import NotificationBell from './NotificationBell';

function playNotificationSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    oscillator.frequency.setValueAtTime(660, ctx.currentTime + 0.1);
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.4);
  } catch (err) { /* non-blocking */ }
}

export default function ChatApp() {
  const { user, logout } = useAuthStore();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [leftPanelRefreshKey, setLeftPanelRefreshKey] = useState(0);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);
  const [chatMode, setChatMode] = useState('ai');
  const [teamUnread, setTeamUnread] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [attentionData, setAttentionData] = useState({ record_badges: {}, total_attention_items: 0 });
  const [attentionPanelOpen, setAttentionPanelOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const prevTotalRef = useRef(null);
  const prevBadgesRef = useRef({});

  const fetchAttention = useCallback(async () => {
    try {
      const data = await getAttention();

      // Detect new attention items (toast + sound)
      const newTotal = data.total_attention_items || 0;
      const prevTotal = prevTotalRef.current;

      if (prevTotal !== null && newTotal > prevTotal) {
        const newBadges = data.record_badges || {};
        const prevBadges = prevBadgesRef.current;

        let hasNewEmail = false;
        let hasNewSMS = false;
        let newCaseName = null;

        for (const [recordId, badge] of Object.entries(newBadges)) {
          const prev = prevBadges[recordId];
          const prevCount = prev?.unread_count || 0;
          if (badge.unread_count > prevCount) {
            if ((badge.channels || []).includes('email')) hasNewEmail = true;
            if ((badge.channels || []).includes('sms')) hasNewSMS = true;
            if (!newCaseName) newCaseName = badge.record_title;
          }
        }

        let toastMsg;
        if (hasNewEmail && hasNewSMS)   toastMsg = `📩 New email & 💬 SMS on ${newCaseName || 'a case'}`;
        else if (hasNewEmail)           toastMsg = `📩 New email on ${newCaseName || 'a case'}`;
        else if (hasNewSMS)             toastMsg = `💬 New SMS on ${newCaseName || 'a case'}`;
        else                            toastMsg = `🔔 ${newTotal - prevTotal} new item(s) need attention`;

        const toastId = Date.now();
        setToasts(prev => [...prev, { id: toastId, message: toastMsg }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toastId)), 6000);

        playNotificationSound();

        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification('Sixty10', { body: toastMsg, icon: '/favicon.ico', tag: 'sixty10-attention' });
          } catch (e) { /* non-blocking */ }
        }
      }
      prevTotalRef.current = newTotal;
      prevBadgesRef.current = data.record_badges || {};

      setAttentionData(data);
    } catch (err) {
      console.error('Attention fetch failed:', err.message);
    }
  }, []);

  // Poll unmatched inbound SMS count
  useEffect(() => {
    const fetchUnmatched = async () => {
      try {
        const token = localStorage.getItem('lf_token');
        const res = await fetch('/api/chat-ai/inbox/unmatched', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setUnmatchedCount((data.messages || []).length);
      } catch(e) {}
    };
    fetchUnmatched();
    const interval = setInterval(fetchUnmatched, 60000);
    return () => clearInterval(interval);
  }, []);

  // Request browser notification permission once
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // On mount + 60s polling
  useEffect(() => {
    fetchAttention();
    const interval = setInterval(fetchAttention, 120000);
    return () => clearInterval(interval);
  }, [fetchAttention]);
  // Refresh priority list every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => setLeftPanelRefreshKey(k => k + 1), 300000);
    return () => clearInterval(interval);
  }, []);
  // Refresh priority list every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => setLeftPanelRefreshKey(k => k + 1), 300000);
    return () => clearInterval(interval);
  }, []);

  // Clear attention for a record when it's selected
  const handleSelectRecord = useCallback(async (record) => {
    setSelectedRecord(record);
    if (record?.record_id) {
      try {
        await clearAttention(record.record_id);
        setAttentionData(prev => {
          const updated = { ...prev.record_badges };
          const removedBadge = updated[record.record_id];
          delete updated[record.record_id];
          const removedCount = removedBadge?.unread_count || 0;
          return {
            ...prev,
            record_badges: updated,
            total_attention_items: Math.max(0, (prev.total_attention_items || 0) - removedCount),
          };
        });
      } catch (err) { /* non-blocking */ }
    }
  }, []);

  // URL param auto-load: ?record=[id] or ?record=[reference_number]
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const recordParam = params.get('record');
    if (!recordParam || recordParam === 'null' || recordParam === 'undefined') return;
    window.history.replaceState({}, '', window.location.pathname);

    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(recordParam);

    const navigate = (id) => {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('panelNavigate', { detail: { record_id: id } }));
      }, 1000);
    };

    if (isUUID) {
      navigate(recordParam);
    } else {
      // Resolve reference number to UUID
      const token = localStorage.getItem('lf_token');
      fetch(`/api/records/resolve?ref=${encodeURIComponent(recordParam)}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => { if (data.record_id) navigate(data.record_id); })
        .catch(err => console.error('[URL param] resolve failed:', err));
    }
  }, []);

  // Reset to AI mode on every record change
  useEffect(() => {
    setChatMode('ai');
  }, [selectedRecord?.record_id]);

  const initials = `${(user?.first_name || '?')[0]}${(user?.last_name || '')[0] || ''}`.toUpperCase();

  return (
    <div className="h-screen w-screen bg-gray-950 flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="h-12 border-b flex items-center justify-between px-4 flex-shrink-0" style={{ background: '#0e0f11', borderBottomColor: '#2a2a35' }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#1a1710' }}>
            <Sparkles className="h-4 w-4" style={{ color: '#c9a84c' }} />
          </div>
          <span className="font-semibold text-sm" style={{ color: '#f5f0e8' }}>Sixty10 AI</span>
        </div>
        <div className="flex items-center gap-3">
          <NotificationBell />
          {unmatchedCount > 0 && (
            <button onClick={() => setInboxOpen(true)}
              className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
              title="Unmatched inbound SMS">
              <MessageSquare className="h-4 w-4 text-green-400" />
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
                style={{ background: '#16a34a' }}>
                {unmatchedCount > 9 ? '9+' : unmatchedCount}
              </span>
            </button>
          )}
          <button
            onClick={() => setAttentionPanelOpen(true)}
            className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
            title="Attention items"
          >
            <span className="text-lg">🔔</span>
            {attentionData.total_attention_items > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white animate-pulse">
                {attentionData.total_attention_items > 99 ? '99+' : attentionData.total_attention_items}
              </span>
            )}
          </button>
          {/* Mode toggle — always visible */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => setChatMode('ai')}
              style={{
                background: chatMode === 'ai' ? '#1a1710' : 'transparent',
                border: `1px solid ${chatMode === 'ai' ? '#c9a84c' : '#3a3000'}`,
                color: chatMode === 'ai' ? '#c9a84c' : '#6b6350',
                fontSize: '10px', fontWeight: 600, padding: '4px 10px',
                borderRadius: '20px', cursor: 'pointer', transition: 'all 0.2s',
              }}
            >AI</button>
            <button
              onClick={() => { setChatMode('team'); setTeamUnread(0); }}
              style={{
                position: 'relative',
                background: chatMode === 'team' ? '#2a1a0a' : 'transparent',
                border: `1px solid ${chatMode === 'team' ? '#c9a84c' : '#3d2a1a'}`,
                color: chatMode === 'team' ? '#c9a84c' : '#8a6540',
                fontSize: '10px', fontWeight: 600, padding: '4px 10px',
                borderRadius: '20px', cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              Team
              {teamUnread > 0 && (
                <span style={{
                  position: 'absolute', top: '-4px', right: '-4px',
                  background: '#c9a84c', color: '#0e0f11',
                  fontSize: '9px', fontWeight: 700,
                  width: '16px', height: '16px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{teamUnread > 9 ? '9+' : teamUnread}</span>
              )}
            </button>
          </div>
          {/* Avatar — click to open profile */}
          <button onClick={() => setProfileOpen(true)}
            style={{
              width: 28, height: 28, borderRadius: '50%', background: '#1a1710',
              border: '1px solid #3d3418', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: 10, fontWeight: 700, color: '#c9a84c', padding: 0,
            }}
            title="My Profile"
          >{initials}</button>
          <button onClick={logout} className="p-1.5 text-gray-500 hover:text-gray-300 rounded transition-colors" title="Sign out">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div className="flex-1 flex overflow-hidden">
        <LeftPanel selectedRecord={selectedRecord} onSelectRecord={handleSelectRecord} refreshKey={leftPanelRefreshKey} profileRefreshKey={profileRefreshKey} attentionData={attentionData} />
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {/* AI Panel */}
          <div style={{
            position: 'absolute', inset: 0,
            transform: chatMode === 'ai' ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: chatMode === 'ai' ? 2 : 0,
            pointerEvents: chatMode === 'ai' ? 'auto' : 'none',
            background: '#0e0f11',
          }}>
            <ChatPanel selectedRecord={selectedRecord} onSelectRecord={handleSelectRecord} onRefreshList={() => setLeftPanelRefreshKey(k => k + 1)} onRefreshProfile={() => setProfileRefreshKey(k => k + 1)} />
          </div>
          {/* Team Chat Panel — only mount when needed */}
          {chatMode === 'team' && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 2 }}>
              <TeamChatPanel
                selectedRecord={selectedRecord}
                onUnread={(count) => { setTeamUnread(prev => prev + count); }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Profile Panel */}
      <InboxModal open={inboxOpen} onClose={() => { setInboxOpen(false); setUnmatchedCount(0); }} />
      <ProfilePanel open={profileOpen} onClose={() => setProfileOpen(false)} />

      {/* Attention Panel */}
      {attentionPanelOpen && (
        <AttentionPanel
          attentionData={attentionData}
          onClose={() => setAttentionPanelOpen(false)}
          onNavigate={(recordId) => {
            window.dispatchEvent(new CustomEvent('panelNavigate', { detail: { record_id: recordId } }));
            handleSelectRecord({ record_id: recordId });
          }}
        />
      )}

      {/* Toasts */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 items-end">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#1a1b1f] border border-[#c9a84c]/40 shadow-2xl animate-slide-in-right"
          >
            <span className="text-sm text-white font-medium">{toast.message}</span>
            <button
              onClick={() => setAttentionPanelOpen(true)}
              className="text-xs text-[#c9a84c] hover:text-[#a67c32] font-semibold whitespace-nowrap"
            >
              View →
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
